import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject, Subscription, from, of, throwError, timer } from 'rxjs';
import { catchError, filter, finalize, map, switchMap, take, takeUntil } from 'rxjs/operators';
import { AppDictionaryService } from './app-dictionary.service';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { FormGroup } from '@angular/forms';
import { MatSnackBar, MatSnackBarRef, TextOnlySnackBar } from '@angular/material/snack-bar';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { Overlay } from '@angular/cdk/overlay';
import { Store } from '@ngrx/store';

import { CommonDialogComponent } from '../components/common-dialog/common-dialog.component';
import { LotExpirationWarningDialogComponent } from '../components/lot-expiration-warning-dialog/lot-expiration-warning-dialog.component';
import { DiscardWarningDialogComponent } from '../components/discard-warning-dialog/discard-warning-dialog.component';
import { DiscardWarningSummary } from '../utils/discard-threshold.util';
import { BannerOptions } from '../models/app.model';
import { ErrorDetails } from '../models/error.model';
import { CommonDialogData, LotExpirationWarningDialogData } from '../models/dialog.model';
import { selectAppState } from '../../app/state/selectors';
import { AppState } from '../../app/state/store';
import { TokenExpirationInfo, TokenExpiryService } from './token-expiry.service';
import { JobStartResponse, PollingProcessingResponse, PollingErrorResponse, PollingRouterName, FileDownloadPayload, PollingResult } from '../models/polling.model';
import { apiPaths } from '../../app/core/constants/api.const';

interface PollingOptions {
  pollIntervalMs?: number;
  timeoutMs?: number;
  skipLoadingAnimation?: boolean;
}

interface PollingConfig {
  pollUrl: string;
  routerName: PollingRouterName;
  uniqueId: string;
  pollIntervalMs: number;
  timeoutMs: number;
}

/**
 * AppUtilityService
 * 
 * A service that provides utility methods for handling application-wide features 
 * such as managing HTTP requests, displaying loading animations, banners, and snackbars, 
 * managing authentication tokens, and handling common UI interactions like dialogs.
 * 
 * This service integrates with the application's state management (NgRx store), 
 * and makes use of Angular's HttpClient, Material Dialog, Snackbar, and Router for 
 * user interactions and API communication.
 */

@Injectable({
    providedIn: 'root'
})
export class AppUtilityService extends AppDictionaryService implements OnDestroy {
    private globalSnackbar!: MatSnackBarRef<TextOnlySnackBar>;
    private snackbarTimeoutId: ReturnType<typeof setTimeout> | null = null;
    private ongoingRequestCount = 0;
    private loadingAnimationControl = new Subject<boolean>();
    private bannerControl = new Subject<BannerOptions>();
    private appState$ = this.store.select(selectAppState);
    private destroy$ = new Subject<boolean>();
    /** Authorization is absent by design: AuthTokenInterceptor attaches it per request. */
    private readonly httpHeaders = new HttpHeaders({
        'Accept': 'application/json',
        'Cache-Control': 'no-cache',
        'Locale': 'en-US'
    });
    private readonly defaultPollIntervalMs = 2000;
    private readonly defaultPollTimeoutMs = 15 * 60 * 1000;

    /**
     * Constructor for AppUtilityService.
     * Initializes HTTP headers, manages token expiration, and subscribes to the app state.
     *
     * @param http HttpClient for making HTTP requests.
     * @param router Router for navigation.
     * @param snackbar MatSnackBar for showing snackbars.
     * @param dialog MatDialog for opening dialogs.
     * @param overlay Overlay for controlling dialog overlays.
     * @param store Store for accessing app state.
     * @param tokenExpiry Watches the session token and owns the re-authorization dialog.
     */
    constructor(
        private http: HttpClient,
        private router: Router,
        private snackbar: MatSnackBar,
        private dialog: MatDialog,
        private overlay: Overlay,
        private store: Store,
        private tokenExpiry: TokenExpiryService
    ) {
        super();

        this.appState$.pipe(
            takeUntil(this.destroy$),
        ).subscribe((appState: AppState | null) => {
            // Hands the token to the watcher, which renews it silently shortly before it
            // lapses rather than waiting for a request to fail.
            this.tokenExpiry.watchToken(appState?.authToken);
        });
    }

    /**
    * Decodes the token to retrieve its expiration info.
    *
    * @param token The authentication token to be decoded.
    * @returns Object containing the expiration date, remaining seconds, and whether it's expired.
    */
    getTokenExpirationInfo(token: string): TokenExpirationInfo | null {
        return this.tokenExpiry.getTokenExpirationInfo(token);
    }

    /**
     * Asks the operator to re-authorize an expired session. Exposed for callers that
     * detect expiry outside an HTTP response.
     */
    promptTokenReauthentication(): void {
        this.tokenExpiry.promptReauthentication();
    }

    /**
    * Displays a banner with a custom message or a default error message.
    * 
    * @param text Optional text to display in the banner.
    */
    showBanner(text?: string) {
        const options = {
            to_show: true,
            text: text || this.errorMessages.serviceFailure
        };
        this.bannerControl.next(options);
    }

    /**
    * Hides the currently displayed banner.
    */
    hideBanner() {
        const options = {
            to_show: false
        };
        this.bannerControl.next(options);
    }

    /**
    * Shows a snackbar with a message.
    * 
    * @param message Optional message to display in the snackbar.
    */
    showSnackbar(message?: string) {
        if (this.snackbarTimeoutId !== null) {
            clearTimeout(this.snackbarTimeoutId);
        }
        this.snackbarTimeoutId = setTimeout(() => {
            this.snackbarTimeoutId = null;
            const snackBarRef = this.snackbar.open(
                message || this.errorMessages.serviceFailure,
                'OK',
                { panelClass: 'bpx-snackbar' });
            this.globalSnackbar = snackBarRef;
        }, 1200);
    }

    /**
     * Dismisses the currently displayed snackbar.
     */
    hideSnackbar() {
        if (this.snackbarTimeoutId !== null) {
            clearTimeout(this.snackbarTimeoutId);
            this.snackbarTimeoutId = null;
        }
        this.globalSnackbar?.dismiss();
    }

    /**
    * Opens a common dialog with custom options.
    * 
    * @param options Data to pass to the dialog.
    * @returns A reference to the opened dialog.
    */
    displayAlertDialog(options?: CommonDialogData) {
        const globalOptions: MatDialogConfig = {
            autoFocus: false,
            panelClass: 'bpx-dialog-container',
            scrollStrategy: this.overlay.scrollStrategies.noop()
        };
        const dialogConfig = { ...globalOptions, data: { ...options } };
        const dialogRef = this.dialog.open(
            CommonDialogComponent,
            dialogConfig
        );
        return dialogRef;
    }

    /**
     * Warns the user that a lot they just imported (calibrator or control) is expired,
     * letting them cancel the import or proceed anyway.
     *
     * @param lotTypeLabel A label describing the kind of lot, e.g. "Calibrator" or "Control".
     * @param lotNumber The lot number that was imported.
     * @param expirationDate The lot's expiration date.
     * @returns An observable emitting `true` if the user chose to proceed, `false` if they cancelled.
     */
    confirmExpiredLotImport(lotTypeLabel: string, lotNumber: string, expirationDate: string): Observable<boolean> {
        const dialogRef = this.dialog.open<LotExpirationWarningDialogComponent, LotExpirationWarningDialogData, boolean>(
            LotExpirationWarningDialogComponent,
            {
                autoFocus: false,
                panelClass: 'bpx-dialog-container',
                data: { lotTypeLabel, lotNumber, expirationDate }
            }
        );
        return dialogRef.afterClosed().pipe(map(confirmed => !!confirmed));
    }

    /**
     * Shows the informational run-quality warning for a study with a high discard rate or a
     * high instrument true-error rate. Single OK button; nothing is gated on the result.
     *
     * @param summary The breach summary to render.
     * @returns A reference to the opened dialog.
     */
    displayDiscardWarningDialog(summary: DiscardWarningSummary): MatDialogRef<DiscardWarningDialogComponent> {
        return this.dialog.open<DiscardWarningDialogComponent, DiscardWarningSummary>(
            DiscardWarningDialogComponent,
            {
                autoFocus: false,
                panelClass: 'bpx-dialog-container',
                scrollStrategy: this.overlay.scrollStrategies.noop(),
                data: summary
            }
        );
    }

    /**
     * Enables or disables form controls based on the provided list.
     * 
     * @param form_group The form group containing the controls.
     * @param control_list List of control names to enable/disable.
     * @param to_enable Boolean flag to enable (true) or disable (false) the controls.
     */
    toggleFormControls(
        form_group: FormGroup,
        control_list: string[],
        to_enable: boolean) {
        const control_count = control_list.length;
        for (let i = 0; i < control_count; i++) {
            const current_control = form_group.get(control_list[i]);
            if (to_enable) {
                current_control?.enable({ emitEvent: false });
            } else {
                current_control?.disable({ emitEvent: false });
            }
        }
    }

    /**
     * Scrolls the page to the specified element.
     * 
     * @param element_ref The CSS selector or element reference to scroll to.
     * @param offset Optional offset to adjust scroll position.
     */
    scrollToElement(element_ref: string | Element, offset = 10) {
        setTimeout(() => {
            const element = typeof element_ref === 'string' ? document.querySelector(element_ref) : element_ref;

            if (element) {
                const scroll_extent = element.getBoundingClientRect().top + window.pageYOffset - offset;
                window.scrollTo(0, scroll_extent);
            }
        }, 200);
    }

    /**
    * Scrolls the page to the top when a route is activated.
    */
    onRouteActivation() {
        window.scrollTo({
            top: 0,
            left: 0,
            behavior: 'smooth'
        });
    }

    /**
     * Navigates to a specified URL.
     * 
     * @param URL The URL to navigate to.
     */
    navigateToURL(URL: string) {
        this.router.navigateByUrl(URL);
    }

    /**
    * Formats a date into a string using the 'en-US' locale.
    * 
    * @param currentDate The date to format.
    * @returns The formatted date string.
    */
    formatDate(currentDate: Date): string {
        return new Intl.DateTimeFormat('en-US').format(currentDate);
    }

    /**
     * Formats a date and time into a string using the 'en-US' locale.
     * 
     * @param currentDate The date to format.
     * @returns The formatted date and time string.
     */
    formatDateTime(currentDate: Date): string {
        return new Intl.DateTimeFormat('en-US', {
            dateStyle: 'short',
            timeStyle: 'short'
        }).format(currentDate);
    }

    /**
     * Unsubscribes from a list of subscriptions.
     * 
     * @param subs The subscriptions to unsubscribe from.
     */
    unsubscribeAll(subs: Subscription[]) {
        const sub_count = subs.length;
        for (let i = 0; i < sub_count; i++) {
            const current_sub = subs[i];
            if (current_sub) {
                current_sub.unsubscribe();
            }
        }
    }

    /**
     * Makes a GET request to the specified URL.
     * 
     * @param url The URL to send the GET request to.
     * @param skipLoadingAnimation Optional flag to skip showing the loading animation.
     * @returns An observable of the HTTP response.
     */
    get<T>(url: string, skipLoadingAnimation?: boolean): Observable<T> {
        this.initialActions();

        return this.http
            .get<T>(url, {
                responseType: 'json',
                headers: this.httpHeaders
            })
            .pipe(
                finalize(() => {
                    if (!skipLoadingAnimation) {
                        this.displayLoadingAnimation(false);
                    }
                })
            );
    }

    /**
     * Makes a POST request to the specified URL with the provided body.
     * 
     * @param url The URL to send the POST request to.
     * @param body The body of the request.
     * @param skipLoadingAnimation Optional flag to skip showing the loading animation.
     * @returns An observable of the HTTP response of type T2.
     */
    post<T1, T2>(url: string, body: T1, skipLoadingAnimation?: boolean): Observable<T2> {
        this.initialActions();

        return this.http
            .post<T2>(url, body, {
                responseType: 'json',
                headers: this.httpHeaders
            })
            .pipe(finalize(() => {
                if (!skipLoadingAnimation) {
                    this.displayLoadingAnimation(false);
                }
            }));
  }

  /**
    * Makes a POST request to the specified URL with the provided body.
    * 
    * @param url The URL to send the POST request to.
    * @param body The body of the request.
    * @param skipLoadingAnimation Optional flag to skip showing the loading animation.
    * @returns An observable of the HTTP response of type Blob.
    */
   postGetCsv<T1>(url: string, body: T1, skipLoadingAnimation?: boolean): Observable<Blob> {
    this.initialActions();

    return this.http
      .post(url, body, {
        responseType: 'blob',
        headers: this.httpHeaders
      })
      .pipe(finalize(() => {
        if (!skipLoadingAnimation) {
          this.displayLoadingAnimation(false);
        }
      }));
  }

  /**
   * Starts a long-running backend job and polls until the JSON response is ready.
   *
   * @param url Target endpoint.
   * @param body Request payload.
   * @param routerName Backend router identifier for polling.
   * @param options Optional polling configuration.
   * @returns Observable emitting the completed response.
   */
  postWithPolling<T>(url: string, body: unknown, routerName: PollingRouterName, options?: PollingOptions): Observable<PollingResult<T>> {
    const pollIntervalMs = options?.pollIntervalMs ?? this.defaultPollIntervalMs;
    const timeoutMs = options?.timeoutMs ?? this.defaultPollTimeoutMs;
    const skipLoadingAnimation = options?.skipLoadingAnimation;

    this.initialActions(skipLoadingAnimation);

    const pollUrl = this.getAPI(apiPaths.study.pollCalculationStatus);

    return this.http.post<JobStartResponse>(url, body, {
      responseType: 'json',
      headers: this.httpHeaders
    }).pipe(
      switchMap(job => {
        this.logPollingEvent('Polling started', { routerName, uniqueId: job.unique_id });
        return this.pollJsonResult<T>({
        pollUrl,
        routerName,
        uniqueId: job.unique_id,
        pollIntervalMs,
        timeoutMs
        }).pipe(
          map(result => ({
            uniqueId: job.unique_id,
            result
          }))
        );
      }),
      finalize(() => {
        if (!skipLoadingAnimation) {
          this.displayLoadingAnimation(false);
        }
      })
    );
  }

  /**
   * Starts a long-running backend job and polls until a file is available for download.
   *
   * @param url Target endpoint.
   * @param body Request payload.
   * @param routerName Backend router identifier for polling.
   * @param options Optional polling configuration.
   * @returns Observable emitting the downloaded file payload.
   */
  postAndPollForFile(url: string, body: unknown, routerName: PollingRouterName, options?: PollingOptions): Observable<FileDownloadPayload> {
    const pollIntervalMs = options?.pollIntervalMs ?? this.defaultPollIntervalMs;
    const timeoutMs = options?.timeoutMs ?? this.defaultPollTimeoutMs;
    const skipLoadingAnimation = options?.skipLoadingAnimation;

    this.initialActions(skipLoadingAnimation);

    const pollUrl = this.getAPI(apiPaths.study.pollCalculationStatus);

    return this.http.post<JobStartResponse>(url, body, {
      responseType: 'json',
      headers: this.httpHeaders
    }).pipe(
      switchMap(job => {
        this.logPollingEvent('File polling started', { routerName, uniqueId: job.unique_id });
        return this.pollFileResult({
        pollUrl,
        routerName,
        uniqueId: job.unique_id,
        pollIntervalMs,
        timeoutMs
        });
      }),
      finalize(() => {
        if (!skipLoadingAnimation) {
          this.displayLoadingAnimation(false);
        }
      })
    );
  }

  private pollJsonResult<T>(config: PollingConfig): Observable<T> {
    const requestUrl = this.buildPollUrl(config.pollUrl, config.routerName, config.uniqueId);
    const startTime = Date.now();

    return timer(0, config.pollIntervalMs).pipe(
      switchMap(() => this.http.get<PollingProcessingResponse | PollingErrorResponse | T>(requestUrl, {
        headers: this.httpHeaders
      })),
      map(response => {
        if (this.isProcessingResponse(response)) {
          this.logPollingEvent('Polling response', { routerName: config.routerName, uniqueId: config.uniqueId, status: response.status });
          if (Date.now() - startTime > config.timeoutMs) {
            throw new Error('Request timed out while waiting for the operation to finish.');
          }
          return null;
        }
        if (this.isErrorResponse(response)) {
          const message = response.message || this.errorMessages.serviceFailure;
          throw new Error(message);
        }
        this.logPollingEvent('Polling complete', { routerName: config.routerName, uniqueId: config.uniqueId });
        return response as T;
      }),
      filter((value): value is T => value !== null),
      take(1),
      catchError(error => throwError(() => error))
    );
  }

  private pollFileResult(config: PollingConfig): Observable<FileDownloadPayload> {
    const requestUrl = this.buildPollUrl(config.pollUrl, config.routerName, config.uniqueId);
    const startTime = Date.now();

    return timer(0, config.pollIntervalMs).pipe(
      switchMap(() => this.http.get(requestUrl, {
        headers: this.httpHeaders,
        responseType: 'blob',
        observe: 'response'
      })),
      switchMap(response => this.handleFilePollResponse(response, startTime, config)),
      filter((value): value is FileDownloadPayload => value !== null),
      take(1),
      catchError(error => throwError(() => error))
    );
  }

  private handleFilePollResponse(response: HttpResponse<Blob>, startTime: number, config: PollingConfig): Observable<FileDownloadPayload | null> {
    const contentType = response.headers.get('content-type') ?? '';
    if (contentType.includes('application/json')) {
      const jsonSource = response.body ? response.body.text() : Promise.resolve('{}');
      return from(jsonSource).pipe(
        map(text => text ? JSON.parse(text) as unknown : {}),
        map(parsed => {
          if (this.isProcessingResponse(parsed)) {
            this.logPollingEvent('File polling response', { routerName: config.routerName, uniqueId: config.uniqueId, status: parsed.status });
            if (Date.now() - startTime > config.timeoutMs) {
              throw new Error('Request timed out while waiting for the operation to finish.');
            }
            return null;
          }
          if (this.isErrorResponse(parsed)) {
            throw new Error(parsed.message || this.errorMessages.serviceFailure);
          }
          throw new Error(this.errorMessages.serviceFailure);
        })
      );
    }

    if (!response.body) {
      return throwError(() => new Error(this.errorMessages.serviceFailure));
    }

    this.logPollingEvent('File polling complete', { routerName: config.routerName, uniqueId: config.uniqueId });
    return of({
      blob: response.body,
      filename: this.extractFilename(response)
    });
  }

  private buildPollUrl(baseUrl: string, routerName: PollingRouterName, uniqueId: string): string {
    const params = new URLSearchParams({
      router_name: routerName,
      unique_id: uniqueId
    });
    return `${baseUrl}?${params.toString()}`;
  }

  private isProcessingResponse(value: unknown): value is PollingProcessingResponse {
    return typeof value === 'object' &&
      value !== null &&
      (value as PollingProcessingResponse).status === 'processing';
  }

  private isErrorResponse(value: unknown): value is PollingErrorResponse {
    return typeof value === 'object' &&
      value !== null &&
      (value as PollingErrorResponse).status === 'error';
  }

  private extractFilename(response: HttpResponse<Blob>): string | undefined {
    const contentDisposition = response.headers.get('content-disposition');
    if (!contentDisposition) {
      return undefined;
    }
    const match = /filename="?([^"]+)"?/i.exec(contentDisposition);
    return match?.[1];
  }

  private logPollingEvent(message: string, data: Record<string, unknown>): void {
    console.log('[Polling]', message, data);
  }

  /**
    * Makes a GET request to the specified URL and returns the raw response body.
    * Use this for any binary or non-JSON download (CSV, PDF, archive); the blob
    * carries whatever content type the server sent.
    *
    * @param url The URL to send the GET request to.
    * @param skipLoadingAnimation Optional flag to skip showing the loading animation.
    * @returns An observable of the HTTP response of type Blob.
    */
  getBlob(url: string, skipLoadingAnimation?: boolean): Observable<Blob> {
    this.initialActions();

    return this.http
      .get(url, {
        responseType: 'blob',
        headers: this.httpHeaders
      })
      .pipe(
        finalize(() => {
          if (!skipLoadingAnimation) {
            this.displayLoadingAnimation(false);
          }
        })
      );
  }

    /**
     * Converts a base64 encoded string to a Blob instance.
     *
     * @param base64String Base64 encoded payload.
     * @param mimeType Blob mime type.
     * @returns Blob constructed from the supplied string.
     */
    public base64ToBlob(base64String: string, mimeType: string): Blob {
        const binary = atob(base64String);
        const buffer = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) {
            buffer[i] = binary.charCodeAt(i);
        }
        return new Blob([buffer], { type: mimeType });
    }

    /**
     * Makes a PUT request to the specified URL with the provided body.
     * 
     * @param url The URL to send the PUT request to.
     * @param body The body of the request.
     * @param skipLoadingAnimation Optional flag to skip showing the loading animation.
     * @returns An observable of the HTTP response of type T2.
     */
    put<T1, T2>(url: string, body: T1, skipLoadingAnimation?: boolean): Observable<T2> {
        this.initialActions();

        return this.http
            .put<T2>(url, body, {
                responseType: 'json',
                headers: this.httpHeaders
            })
            .pipe(finalize(() => {
                if (!skipLoadingAnimation) {
                    this.displayLoadingAnimation(false);
                }
            }));
    }

    /**
     * Makes a DELETE request to the specified URL.
     * 
     * @param url The URL to send the DELETE request to.
     * @param skipLoadingAnimation Optional flag to skip showing the loading animation.
     * @returns An observable of the HTTP response of type T.
     */
    delete<T>(url: string, skipLoadingAnimation?: boolean): Observable<T> {
        this.initialActions();

        return this.http
            .delete<T>(url, {
                responseType: 'json',
                headers: this.httpHeaders
            })
            .pipe(finalize(() => {
                if (!skipLoadingAnimation) {
                    this.displayLoadingAnimation(false);
                }
            }));
    }

    /**
     * Formats the error response into a standard ErrorDetails object.
     * 
     * @param error The error object to format.
     * @returns An ErrorDetails object containing a formatted message.
     */
    public getError(error: Error): ErrorDetails {
        const httpError = error as { error?: { detail?: unknown; message?: unknown; error?: unknown } };
        const detail = httpError?.error?.detail ?? httpError?.error?.message ?? httpError?.error?.error;
        const detailMessage = this.formatErrorDetail(detail);
        if (detailMessage) {
            return { message: detailMessage } as ErrorDetails;
        }
        return {
            message: `${this.errorMessages.serviceFailure} ${error?.message ?? ''}`
        } as ErrorDetails;
    }

    private formatErrorDetail(detail: unknown): string {
        if (typeof detail === 'string') {
            return detail;
        }
        if (Array.isArray(detail)) {
            const messages = detail
                .map((item) => this.formatErrorDetail(item))
                .filter((msg) => !!msg);
            return messages.join('; ');
        }
        if (detail && typeof detail === 'object') {
            const typedDetail = detail as { msg?: unknown; detail?: unknown; loc?: unknown };
            if (typeof typedDetail.msg === 'string') {
                const loc = Array.isArray(typedDetail.loc)
                    ? typedDetail.loc
                        .filter((part) => part !== null && part !== undefined)
                        .map((part) => String(part))
                        .filter((part) => part !== 'body')
                        .join('.')
                    : '';
                return loc ? `${loc}: ${typedDetail.msg}` : typedDetail.msg;
            }
            if (typedDetail.detail !== undefined) {
                return this.formatErrorDetail(typedDetail.detail);
            }
            try {
                return JSON.stringify(detail);
            } catch {
                return String(detail);
            }
        }
        return '';
    }

    /**
     * Determines if the user is authenticated by checking the app state.
     * 
     * @returns An observable boolean indicating if the user is authenticated.
     */
    public isAuthenticated(): Observable<boolean> {
        return this.appState$.pipe(
            map(appState => appState.isAuthenticated)
        );
    }


    /**
     * Determines if a study is loaded by checking the app state.
     * 
     * @returns An observable boolean indicating if a study is loaded.
     */
    public isStudyLoaded(): Observable<boolean> {
        return this.appState$.pipe(
            map(appState => {
                const study = appState.selectedStudy;
                if (!study) {
                    return false;
                }

                // Persisted studies always have a studyId.
                if (study.studyId) {
                    return true;
                }

                // Unsaved in-progress studies (started via Run Data flow) should also
                // count as "loaded" once meaningful study content exists.
                const hasReportItems = (study.reportContentItems?.reportContentItems?.length ?? 0) > 0;
                const hasRunSamples = (study.runSamples?.length ?? 0) > 0;
                const hasFileMetadata =
                    (study.patientFileMetadata?.length ?? 0) > 0 ||
                    (study.calFileMetadata?.length ?? 0) > 0 ||
                    (study.controlFileMetadata?.length ?? 0) > 0;

                return hasReportItems || hasRunSamples || hasFileMetadata;
            })
        );
    }

    /**
     * Initiates some initial actions such as hiding snackbars and banners, and optionally showing a loading animation.
     * 
     * @param skipLoadingAnimation Optional flag to skip showing the loading animation.
     */
    private initialActions(skipLoadingAnimation?: boolean) {
        // Hide snackbars and banners if any
        this.hideSnackbar();
        this.hideBanner();

        if (!skipLoadingAnimation) {
            this.displayLoadingAnimation(true);
        }
    }

    /**
     * Controls the display of the loading animation based on the ongoing request count.
     * 
     * @param isLoading Boolean indicating whether a loading animation should be shown.
     */
    private displayLoadingAnimation(isLoading: boolean) {
        this.ongoingRequestCount = isLoading
            ? this.ongoingRequestCount + 1
            : Math.max(0, this.ongoingRequestCount - 1);
        this.loadingAnimationControl.next(this.ongoingRequestCount > 0);
    }

    /**
     * Cleans up resources when the service is destroyed.
     * 
     * Unsubscribes from all observables to prevent memory leaks.
     */
    ngOnDestroy(): void {
        this.destroy$.next(true);
        this.destroy$.complete();
        if (this.snackbarTimeoutId !== null) {
            clearTimeout(this.snackbarTimeoutId);
        }
    }
}
